import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Menubar from "../Components/Menubar";
import Totop from "../Components/Totop";
import Footer from "../Components/Footer";
import { imsi } from "../..";
import { DEFAULT_LANG } from "../../i18n";
import "../../index.css";
import "./Main.css";
import "./HomeV3.css";

import imgCro from "../../assets/images/00main/main-sub6.svg";
import imgRpm from "../../assets/images/00main/main-sub5.svg";
import imgHealthcare from "../../assets/images/mainimage.png";
import solOsto from "../../assets/images/03rpm/service1_card.png";
import solAtti from "../../assets/images/03rpm/service2_card.png";
import solHealiary from "../../assets/images/03rpm/service3_card_fit.png";
import solLars from "../../assets/images/03rpm/lars_care.svg";
import solRisk from "../../assets/images/03rpm/risk_prediction.svg";

import logoSuh from "../../assets/images/02about_partners/01suh.png";
import logoGsh from "../../assets/images/02about_partners/02gsh.png";
import logoAuh from "../../assets/images/02about_partners/03auh.png";
import logoEumc from "../../assets/images/02about_partners/04eumc.png";
import logoCuh from "../../assets/images/02about_partners/05cuh.png";
import logoSsh from "../../assets/images/02about_partners/07ssh.png";
import logoKcsg from "../../assets/images/02about_partners/08kcsg1.png";
import logoKgog from "../../assets/images/02about_partners/KGOG.png";
import { postTitle } from "../News/newsText";

const getLangFromPath = (pathname) => {
  const m = pathname.match(/^\/(kr|en)(\/|$)/);
  return m ? m[1] : DEFAULT_LANG;
};

const renderMultiline = (text) =>
  text.split("\n").map((line, i, arr) => (
    <React.Fragment key={i}>
      {line}
      {i < arr.length - 1 && <br />}
    </React.Fragment>
  ));

/* 숫자가 올라가는 효과: "7,000+" 처럼 기호가 섞여 있어도 숫자 부분만 셉니다. */
function CountUpValue({ value }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(value);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const match = String(value).match(/^([^\d]*)([\d,]+)(.*)$/);
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!match || reduceMotion || typeof IntersectionObserver === "undefined") {
      setShown(value);
      return;
    }
    const [, prefix, digits, suffix] = match;
    const target = Number(digits.replace(/,/g, ""));
    const format = (n) => `${prefix}${n.toLocaleString("ko-KR")}${suffix}`;
    setShown(format(0));

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        const duration = 1200;
        const start = performance.now();
        const step = (now) => {
          const p = Math.min((now - start) / duration, 1);
          // 끝으로 갈수록 천천히
          const eased = 1 - Math.pow(1 - p, 3);
          setShown(format(Math.round(target * eased)));
          if (p < 1) window.requestAnimationFrame(step);
        };
        window.requestAnimationFrame(step);
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span className="v3-number-value" ref={ref}>
      {shown}
    </span>
  );
}

const BUSINESS = [
  { key: "cro", img: imgCro, link: "/clinicalresearch" },
  { key: "rpm", img: imgRpm, link: "/rpmsolution" },
  { key: "healthcare", img: imgHealthcare, link: "/healthcare" },
];

const SOLUTIONS = [
  { key: "ostoCare", img: solOsto, link: "/rpmsolution" },
  { key: "atti", img: solAtti, link: "/rpmsolution" },
  { key: "larsCare", img: solLars, link: "/rpmsolution" },
  { key: "healiary", img: solHealiary, link: "/healthcare" },
  { key: "riskPrediction", img: solRisk, link: "/healthcare" },
];

const NUMBERS = ["patients", "trials", "solutions"];

const PARTNER_LOGOS = [
  logoSuh, logoGsh, logoAuh, logoEumc, logoCuh, logoSsh, logoKcsg, logoKgog,
];

export default function HomeV3() {
  const history = useHistory();
  const { t } = useTranslation();
  const lang = getLangFromPath(useLocation().pathname);
  const goto = (p) => history.push(`/${lang}${p}`);

  const [news, setNews] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    // 뉴스 메뉴(News Release)와 같은 데이터: 최신 3건
    axios
      .get(`${imsi}/api/boardList`, { params: { type: "News", page: 1 } })
      .then((res) => {
        const list = [...(res.data.board_data || [])].sort((a, b) =>
          String(b.reg_datetime).localeCompare(String(a.reg_datetime))
        );
        setNews(list.slice(0, 3));
      })
      .catch((err) => console.log(err));
  }, []);

  const scrollToBusiness = () => {
    const el = document.getElementById("v3-business");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="v3-root">
      <Menubar slideIndex={0} />
      <Totop />

      {/* HERO: v1 브랜드 그래픽 유지, 레이아웃만 반응형 */}
      <section className="v3-hero">
        <div className="v3-hero-art" aria-hidden="true" />
        <div className="v3-wrap v3-hero-inner">
          <h1 className="v3-hero-title">
            <span className="v3-brand">HERINGS</span>
            {t("homeV3.hero.titleAfterBrand")}
            <br />
            {t("homeV3.hero.line2")}
            <br />
            <span className="v3-accent">{t("homeV3.hero.line3")}</span>
          </h1>
          <p className="v3-hero-sub">{t("homeV3.hero.sub")}</p>
          <div className="v3-hero-actions">
            <button className="v3-btn v3-btn-primary" onClick={scrollToBusiness}>
              {t("homeV3.hero.ctaPrimary")}
            </button>
            <button className="v3-btn v3-btn-line" onClick={() => goto("/contactus")}>
              {t("homeV3.hero.ctaSecondary")}
            </button>
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="v3-partners">
        <p className="v3-partners-label">{t("homeV3.partners.label")}</p>
        <div className="v3-marquee">
          <div className="v3-marquee-track">
            {[...PARTNER_LOGOS, ...PARTNER_LOGOS].map((src, i) => (
              <div className="v3-marquee-item" key={i}>
                <img src={src} alt="" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUSINESS AREAS: v1의 좌우 교차 사진 레이아웃 */}
      <section className="v3-business" id="v3-business">
        <div className="v3-wrap">
          <div className="v3-head">
            <p className="v3-eyebrow">{t("homeV3.business.eyebrow")}</p>
            <h2 className="v3-h2">{t("homeV3.business.title")}</h2>
            <p className="v3-lead">{t("homeV3.business.lead")}</p>
          </div>
          {BUSINESS.map((b, i) => (
            <div className={`v3-biz ${i % 2 === 1 ? "v3-biz-reverse" : ""}`} key={b.key}>
              <div className="v3-biz-media">
                <img src={b.img} alt={t(`homeV3.business.items.${b.key}.tag`)} />
              </div>
              <div className="v3-biz-body">
                <p className="v3-biz-tag">{t(`homeV3.business.items.${b.key}.tag`)}</p>
                <h3 className="v3-biz-title">
                  {renderMultiline(t(`homeV3.business.items.${b.key}.title`))}
                </h3>
                <p className="v3-biz-desc">{t(`homeV3.business.items.${b.key}.desc`)}</p>
                <button className="v3-btn v3-btn-primary" onClick={() => goto(b.link)}>
                  {t(`homeV3.business.items.${b.key}.btn`)}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* NUMBERS: 회사 실적(회사소개 Case Study 기준) */}
      <section className="v3-numbers">
        <div className="v3-wrap">
          <div className="v3-head">
            <p className="v3-eyebrow">{t("homeV3.numbers.eyebrow")}</p>
            <h2 className="v3-h2">{t("homeV3.numbers.title")}</h2>
          </div>
          <div className="v3-numbers-grid">
            {NUMBERS.map((k) => (
              <div className="v3-number" key={k}>
                <CountUpValue value={t(`homeV3.numbers.items.${k}.value`)} />
                <span className="v3-number-label">
                  {renderMultiline(t(`homeV3.numbers.items.${k}.label`))}
                </span>
              </div>
            ))}
          </div>
          <button className="v3-text-link" onClick={() => goto("/whoweare")}>
            {t("homeV3.numbers.link")} &rsaquo;
          </button>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="v3-solutions">
        <div className="v3-wrap">
          <div className="v3-head">
            <p className="v3-eyebrow">{t("main.solutions.label")}</p>
            <h2 className="v3-h2">{t("main.solutions.title")}</h2>
          </div>
          <div className="v3-sol-grid">
            {SOLUTIONS.map((s) => (
              <button className="v3-sol-card" key={s.key} onClick={() => goto(s.link)}>
                <div className="v3-sol-img">
                  <img src={s.img} alt={t(`main.solutions.items.${s.key}.title`)} />
                </div>
                <div className="v3-sol-body">
                  <p className="v3-sol-title">{t(`main.solutions.items.${s.key}.title`)}</p>
                  <p className="v3-sol-desc">{t(`main.solutions.items.${s.key}.desc`)}</p>
                  <span className="v3-sol-link">{t("main.solutions.learnMore")} &rsaquo;</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* NEWS: 뉴스 메뉴와 같은 데이터 */}
      {news.length > 0 && (
        <section className="v3-news">
          <div className="v3-wrap">
            <div className="v3-news-head">
              <div>
                <p className="v3-eyebrow">{t("homeV3.news.eyebrow")}</p>
                <h2 className="v3-h2">{t("homeV3.news.title")}</h2>
              </div>
              <button className="v3-btn v3-btn-line" onClick={() => goto("/news")}>
                {t("homeV3.news.viewAll")}
              </button>
            </div>
            <div className="v3-news-grid">
              {news.map((n) => (
                <a
                  className="v3-news-card"
                  key={n.no}
                  href={n.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="v3-news-img">
                    {n.img && (
                      <img
                        src={n.img}
                        alt=""
                        loading="lazy"
                        onLoad={(e) => {
                          // 세로 사진이면 잘리지 않게 전체 표시
                          const el = e.currentTarget;
                          if (el.naturalHeight > el.naturalWidth) {
                            el.classList.add("is-portrait");
                          }
                        }}
                      />
                    )}
                  </div>
                  <div className="v3-news-body">
                    <span className="v3-news-date">{String(n.reg_datetime).slice(0, 10)}</span>
                    <p className="v3-news-title">{postTitle(n)}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT: v1 배너 재사용 */}
      <div className="contact-banner">
        <div className="contact-banner-inner">
          <div className="contact-banner-left">
            <p className="contact-banner-label">{t("main.contact.label")}</p>
            <p className="contact-banner-title">{renderMultiline(t("main.contact.title"))}</p>
            <p className="contact-banner-subtitle">{t("homeV3.contactSubtitle")}</p>
          </div>
          <div className="contact-banner-right">
            <div className="contact-banner-card">
              <div className="contact-banner-card-item">
                <span className="contact-banner-card-label">{t("main.contact.emailLabel")}</span>
                <span className="contact-banner-card-value">herings@heringsglobal.com</span>
              </div>
              <div className="contact-banner-card-item">
                <span className="contact-banner-card-label">{t("main.contact.addressLabel")}</span>
                <span className="contact-banner-card-value">
                  {renderMultiline(t("main.contact.address"))}
                </span>
              </div>
              <div className="contact-banner-btn" onClick={() => goto("/contactus")}>
                {t("main.contact.btn")}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
